import { type CandidateStatEntry } from "@crowdcoursing/api/openapi";
import { Box, Card, CardContent, Grid, Typography } from "@mui/material";
import { type ComponentProps } from "react";

import { StudentCandidacyMetricsChart } from "../../../organisms/Stats/Shared/StudentCandidacyMetricsChart";

type StudentCandidacyDashboardCardType = "number" | "percentage";

interface StudentCandidacyDashboardCardProps {
  title: string;
  data: number;
  status: Utils.QueryStatus;
  type?: StudentCandidacyDashboardCardType;
}

const StudentCandidacyDashboardCard = ({
  title,
  data,
  status,
  type = "number",
}: StudentCandidacyDashboardCardProps) => {
  return (
    <Card elevation={1} sx={{ height: 80 }}>
      <CardContent sx={{ py: 0.5 }}>
        <Box
          gap={0.5}
          sx={{ alignItems: "center", display: "flex", flexDirection: "column", justifyContent: "center" }}
        >
          <Typography sx={{ fontWeight: "bold" }} variant="caption">
            {title}
          </Typography>
          <Box sx={{ alignItems: "center", display: "flex", justifyContent: "center" }}>
            <Typography variant="h5">
              {status === "pending" ? "..." : status === "error" ? "Error" : data}
              {type === "percentage" && "%"}
            </Typography>
          </Box>
        </Box>
      </CardContent>
    </Card>
  );
};

const calculatePlacementRate = (metrics: CandidateStatEntry[] | null) => {
  const mostRecentStat = metrics?.[metrics.length - 1];
  if (!mostRecentStat?.metrics?.studentsPlaced || !mostRecentStat?.metrics?.totalStudents) {
    return 0;
  }
  const rate = mostRecentStat.metrics.studentsPlaced / mostRecentStat.metrics.totalStudents;
  // Convert to percentage and round to 1 decimal place
  return Math.round(rate * 1000) / 10;
};

export interface StudentCandidacyDashboardProps {
  bubbleId: string;
  title?: string;
  subtitle?: string;
  metrics: CandidateStatEntry[] | null;
  status: Utils.QueryStatus;
  onTimeRangeChange: ComponentProps<typeof StudentCandidacyMetricsChart>["onTimeRangeChange"];
}

const defaultTitle = "Candidacy Pipeline";
const defaultSubtitle = "Track candidate progress through the employment pipeline";

export const StudentCandidacyDashboard = ({
  title = defaultTitle,
  subtitle = defaultSubtitle,
  metrics,
  status,
  bubbleId,
  onTimeRangeChange,
}: StudentCandidacyDashboardProps) => {
  // Fetch student candidacy metrics
  const loading = status === "pending";
  const error = status === "error";

  const mostRecentStat = metrics?.[metrics.length - 1];

  const placementRate = calculatePlacementRate(metrics);

  const cards: StudentCandidacyDashboardCardProps[] = [
    {
      data: mostRecentStat?.metrics?.totalStudents || 0,
      status,
      title: "Total Candidates",
    },
    {
      data: mostRecentStat?.metrics?.studentsWithResume || 0,
      status,
      title: "Résumé Completion",
    },
    {
      data: mostRecentStat?.metrics?.studentsWithInterviewRequests || 0,
      status,
      title: "Interview Requests",
    },
    {
      data: placementRate,
      status,
      title: "Placement Rate",
      type: "percentage",
    },
  ];

  return (
    <Box sx={{ mb: 2, pt: 1 }}>
      {/* How to Read This Data */}
      <Box sx={{ bgcolor: "background.paper", border: 1, borderColor: "divider", borderRadius: 2, mb: 3, p: 2 }}>
        <Typography sx={{ mb: 1 }} variant="h6">
          How to Read This Data
        </Typography>
        <Typography color="text.secondary" variant="body2">
          <strong>Summary Cards:</strong> Total Candidates, Résumé Completion, Interview Requests, and Placement Rate
          show the current state of the pipeline.
          <br />
          <br />
          <strong>Chart Views:</strong>
          <br />• <strong>Pipeline:</strong> Shows the current count at each stage of the candidacy funnel (candidates →
          résumés → interest → interviews → placed).
          <br />• <strong>Funnel:</strong> Visualizes the pipeline as a funnel chart, showing how candidates progress
          through each stage.
          <br />• <strong>Percentage:</strong> Shows each stage as a percentage of total candidates, highlighting
          conversion rates.
          <br />• <strong>Time Series:</strong> Tracks how each metric has changed over time within the selected date
          range.
          <br />
          <br />
          <em>Use these views to identify bottlenecks in the pipeline and track progress over time.</em>
        </Typography>
      </Box>

      <Grid container spacing={2}>
        <Grid size={12}>
          <Typography sx={{ mb: 0.5 }} variant="h6">
            {title}
          </Typography>
          <Typography color="text.secondary" variant="body2">
            {subtitle}
          </Typography>
        </Grid>

        {/* Key Metrics Cards */}
        <Grid container spacing={1.5} size={12}>
          {cards.map((card, index) => (
            <Grid
              key={index}
              size={{
                md: 3,
                sm: 6,
                xs: 12,
              }}
            >
              <StudentCandidacyDashboardCard {...card} />
            </Grid>
          ))}
        </Grid>

        {/* Chart Component */}
        <Grid size={12}>
          <StudentCandidacyMetricsChart
            error={error ? "Error" : undefined}
            loading={loading}
            onTimeRangeChange={onTimeRangeChange}
            timeSeriesData={metrics || []}
          />
        </Grid>

        {/* Footer with metadata */}
        <Grid size={12}>
          <Typography color="text.secondary" variant="caption">
            Data for ID: {bubbleId} ({metrics?.length} data points)
          </Typography>
        </Grid>
      </Grid>
    </Box>
  );
};
